import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Save, Sparkles, Plus, GripVertical, Trash2, ArrowLeft, Eye, Send } from "lucide-react";

const BLOCK_TYPES = [
  { type: "paragraph", label: "Paragraf" },
  { type: "heading", label: "Başlık", level: 2 },
  { type: "quote", label: "Alıntı" },
  { type: "list", label: "Liste", style: "ul" },
  { type: "callout", label: "Çağrı", variant: "tip" },
  { type: "image", label: "Görsel" },
];

const empty = { type: "paragraph", text: "" };

export default function AdminArticleEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cats, setCats] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiKeywords, setAiKeywords] = useState("");

  useEffect(() => {
    (async () => {
      const [a, c] = await Promise.all([
        api.get(`/admin/articles/${id}`),
        api.get(`/admin/categories`),
      ]);
      setDoc(a.data);
      setCats(c.data || []);
    })();
  }, [id]);

  if (!doc) return <p className="text-cc-muted">Yükleniyor…</p>;

  const tr = doc.translations?.tr || {};

  const setTr = (patch) => setDoc((d) => ({
    ...d, translations: { ...d.translations, tr: { ...d.translations.tr, ...patch, enabled: true } },
  }));

  const blocks = tr.content_blocks || [];

  const addBlock = (b) => {
    const newBlock = { ...b };
    if (newBlock.type === "list") newBlock.items = [""];
    if (newBlock.type === "callout") newBlock.text = "";
    if (newBlock.type === "image") newBlock.url = "";
    setTr({ content_blocks: [...blocks, newBlock] });
  };
  const updateBlock = (i, patch) => {
    const next = blocks.slice();
    next[i] = { ...next[i], ...patch };
    setTr({ content_blocks: next });
  };
  const removeBlock = (i) => {
    const next = blocks.slice();
    next.splice(i, 1);
    setTr({ content_blocks: next });
  };
  const moveBlock = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = blocks.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setTr({ content_blocks: next });
  };

  const save = async (publish = false) => {
    setSaving(true);
    try {
      const payload = {
        type: doc.type,
        status: publish ? "published" : doc.status,
        category_id: doc.category_id || null,
        featured_image_url: doc.featured_image_url || null,
        noindex: doc.noindex || false,
        translations: doc.translations,
      };
      const { data } = await api.put(`/admin/articles/${id}`, payload);
      setDoc(data);
    } finally { setSaving(false); }
  };

  const aiDraft = async () => {
    if (!aiTopic) return;
    setAiBusy(true);
    try {
      const { data: job } = await api.post("/admin/ai/draft", {
        topic: aiTopic,
        keywords: aiKeywords.split(",").map((s) => s.trim()).filter(Boolean),
        language: "tr",
      });
      const jobId = job.job_id;
      // Poll up to 120 seconds
      let result = null;
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: st } = await api.get(`/admin/ai/job/${jobId}`);
        if (st.status === "done") { result = st.result; break; }
        if (st.status === "error") { throw new Error(st.error || "AI hatası"); }
      }
      if (result && result.blocks) {
        setTr({
          title: result.title || tr.title,
          seo_title: result.seo_title || tr.seo_title,
          meta_description: result.meta_description || tr.meta_description,
          excerpt: result.excerpt || tr.excerpt,
          content_blocks: result.blocks,
          faq: result.faq || tr.faq || [],
        });
      } else if (!result) {
        alert("AI zaman aşımı. Tekrar deneyin.");
      }
    } catch (e) {
      alert("AI hatası: " + (e.response?.data?.detail || e.message));
    } finally { setAiBusy(false); }
  };

  const aiSeo = async () => {
    setAiBusy(true);
    try {
      const snippet = (blocks || []).filter((b) => b.text).map((b) => b.text).join(" ");
      const { data } = await api.post("/admin/ai/seo", {
        title: tr.title, content_snippet: snippet, language: "tr",
      });
      setTr({
        seo_title: data.seo_title || tr.seo_title,
        meta_description: data.meta_description || tr.meta_description,
        faq: data.faq || tr.faq || [],
      });
    } catch (e) {
      alert("AI hatası: " + (e.response?.data?.detail || e.message));
    } finally { setAiBusy(false); }
  };

  const aiImprove = async (i) => {
    const block = blocks[i];
    if (!block.text) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/ai/improve", {
        text: block.text, instruction: "Akıcılık ve zarafet için düzenle.", language: "tr",
      });
      updateBlock(i, { text: data.text });
    } finally { setAiBusy(false); }
  };

  return (
    <div data-testid="admin-article-editor" className="flex gap-8">
      {/* Main editor */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-6">
          <Link to="/admin/articles" className="text-sm text-cc-muted hover:text-cc-text flex items-center gap-1">
            <ArrowLeft size={14} /> Tüm yazılar
          </Link>
          <div className="flex items-center gap-3">
            <span className={`text-[10px] uppercase tracking-overline px-2 py-1 ${doc.status === "published" ? "bg-cc-sepia/15 text-cc-sepia" : "bg-cc-border/50 text-cc-muted"}`}>{doc.status}</span>
            {doc.status === "published" && tr.slug && (
              <Link to={`/${doc.type === "guide" ? "rehber" : "journal"}/${tr.slug}`} target="_blank" className="text-xs text-cc-muted hover:text-cc-text flex items-center gap-1">
                <Eye size={14} /> Önizle
              </Link>
            )}
            <button onClick={() => save(false)} disabled={saving} className="cc-btn cc-btn-ghost text-[10px] py-2 px-4" data-testid="save-draft">
              <Save size={14} /> {saving ? "..." : "Kaydet"}
            </button>
            <button onClick={() => save(true)} disabled={saving} className="cc-btn text-[10px] py-2 px-4" data-testid="publish">
              <Send size={14} /> Yayınla
            </button>
          </div>
        </div>

        <div className="bg-white border border-admin-border p-8 mb-6">
          <select value={doc.type} onChange={(e) => setDoc({ ...doc, type: e.target.value })}
                  className="text-[10px] tracking-overline uppercase text-cc-sepia bg-transparent border-none mb-4" data-testid="art-type">
            <option value="journal">Journal</option>
            <option value="guide">Rehber</option>
          </select>
          <input
            value={tr.title || ""}
            onChange={(e) => setTr({ title: e.target.value })}
            placeholder="Başlık..."
            className="w-full text-4xl md:text-5xl font-serif text-cc-text bg-transparent border-none outline-none mb-3"
            data-testid="art-title"
          />
          <input
            value={tr.excerpt || ""}
            onChange={(e) => setTr({ excerpt: e.target.value })}
            placeholder="Kısa özet (excerpt)..."
            className="w-full text-base text-cc-muted bg-transparent border-none outline-none italic"
            data-testid="art-excerpt"
          />
        </div>

        {/* Blocks */}
        <div className="space-y-3" data-testid="art-blocks">
          {blocks.map((b, i) => (
            <div key={i} className="bg-white border border-admin-border p-4 group">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <GripVertical size={14} className="text-cc-muted opacity-50" />
                  <span className="text-[10px] tracking-overline uppercase text-cc-muted">{b.type}{b.level ? ` h${b.level}` : ""}</span>
                </div>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={() => moveBlock(i, -1)} className="text-xs text-cc-muted hover:text-cc-text" data-testid={`block-up-${i}`}>↑</button>
                  <button onClick={() => moveBlock(i, 1)} className="text-xs text-cc-muted hover:text-cc-text" data-testid={`block-down-${i}`}>↓</button>
                  {b.text && (
                    <button onClick={() => aiImprove(i)} disabled={aiBusy} className="text-xs text-cc-sepia flex items-center gap-1" data-testid={`block-ai-${i}`}><Sparkles size={12} /> AI</button>
                  )}
                  <button onClick={() => removeBlock(i)} className="text-cc-muted hover:text-red-600" data-testid={`block-del-${i}`}><Trash2 size={14} /></button>
                </div>
              </div>
              <BlockEditor block={b} onChange={(p) => updateBlock(i, p)} />
            </div>
          ))}
        </div>

        {/* Add block bar */}
        <div className="mt-6 bg-white border border-admin-border p-4">
          <div className="text-[10px] tracking-overline uppercase text-cc-muted mb-3">Blok ekle</div>
          <div className="flex flex-wrap gap-2">
            {BLOCK_TYPES.map((b) => (
              <button key={b.label} onClick={() => addBlock(b)}
                className="text-xs px-3 py-2 border border-admin-border hover:bg-admin-bg flex items-center gap-1"
                data-testid={`add-block-${b.type}`}>
                <Plus size={12} /> {b.label}
              </button>
            ))}
          </div>
        </div>

        {/* SEO + Meta */}
        <div className="mt-8 bg-white border border-admin-border p-6">
          <div className="text-[10px] tracking-overline uppercase text-cc-sepia mb-4">SEO & Meta</div>
          <label className="block mb-4">
            <span className="text-[10px] tracking-overline uppercase text-cc-muted">SEO Başlık</span>
            <input value={tr.seo_title || ""} onChange={(e) => setTr({ seo_title: e.target.value })} className="cc-input mt-1" data-testid="seo-title" />
            <div className="text-[10px] text-cc-muted mt-1">{(tr.seo_title || "").length}/60</div>
          </label>
          <label className="block mb-4">
            <span className="text-[10px] tracking-overline uppercase text-cc-muted">Meta Açıklama</span>
            <textarea value={tr.meta_description || ""} onChange={(e) => setTr({ meta_description: e.target.value })} className="cc-input mt-1 min-h-[60px]" data-testid="seo-desc" />
            <div className="text-[10px] text-cc-muted mt-1">{(tr.meta_description || "").length}/160</div>
          </label>
          <label className="block mb-4">
            <span className="text-[10px] tracking-overline uppercase text-cc-muted">Slug</span>
            <input value={tr.slug || ""} onChange={(e) => setTr({ slug: e.target.value })} className="cc-input mt-1" data-testid="seo-slug" placeholder="(boş bırakılırsa otomatik üretilir)" />
          </label>
          <label className="block mb-4">
            <span className="text-[10px] tracking-overline uppercase text-cc-muted">Featured Image URL</span>
            <input value={doc.featured_image_url || ""} onChange={(e) => setDoc({ ...doc, featured_image_url: e.target.value })} className="cc-input mt-1" data-testid="seo-image" placeholder="https://..." />
          </label>
          <label className="flex items-center gap-2 mt-3 text-sm">
            <input type="checkbox" checked={!!doc.noindex} onChange={(e) => setDoc({ ...doc, noindex: e.target.checked })} data-testid="seo-noindex" />
            <span className="text-cc-muted">noindex</span>
          </label>
        </div>

        {/* Category */}
        <div className="mt-6 bg-white border border-admin-border p-6">
          <div className="text-[10px] tracking-overline uppercase text-cc-sepia mb-4">Kategori</div>
          <select value={doc.category_id || ""} onChange={(e) => setDoc({ ...doc, category_id: e.target.value || null })}
                  className="cc-input" data-testid="cat-select">
            <option value="">(Seçilmedi)</option>
            {cats.filter((c) => c.type === doc.type).map((c) => (
              <option key={c.id} value={c.id}>{c.translations?.tr?.name}</option>
            ))}
          </select>
        </div>

        {/* FAQ */}
        <div className="mt-6 bg-white border border-admin-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] tracking-overline uppercase text-cc-sepia">SSS</div>
            <button onClick={() => setTr({ faq: [...(tr.faq || []), { q: "", a: "" }] })} className="text-xs text-cc-sepia">+ Ekle</button>
          </div>
          {(tr.faq || []).map((f, i) => (
            <div key={i} className="mb-4 pb-4 border-b border-admin-border last:border-0">
              <input value={f.q} onChange={(e) => {
                const next = (tr.faq || []).slice(); next[i] = { ...next[i], q: e.target.value }; setTr({ faq: next });
              }} className="cc-input mb-2" placeholder="Soru" />
              <textarea value={f.a} onChange={(e) => {
                const next = (tr.faq || []).slice(); next[i] = { ...next[i], a: e.target.value }; setTr({ faq: next });
              }} className="cc-input min-h-[60px]" placeholder="Cevap" />
              <button onClick={() => { const next = (tr.faq || []).slice(); next.splice(i, 1); setTr({ faq: next }); }}
                      className="text-xs text-red-500 mt-2">Sil</button>
            </div>
          ))}
        </div>
      </div>

      {/* AI Sidebar */}
      <aside className="w-[320px] flex-shrink-0 sticky top-6 self-start" data-testid="ai-sidebar">
        <div className="bg-white border border-admin-border p-5">
          <div className="flex items-center gap-2 text-cc-sepia mb-4">
            <Sparkles size={16} /> <span className="text-[10px] tracking-overline uppercase">AI Asistan</span>
          </div>
          <p className="text-xs text-cc-muted mb-4">Claude Sonnet 4.5. Sadece taslak üretir; siz onaylamadan yayınlanmaz.</p>

          <label className="block mb-3">
            <span className="text-[10px] tracking-overline uppercase text-cc-muted">Konu</span>
            <input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} className="cc-input mt-1" placeholder="Örn: Ihlara Vadisi yürüyüşü" data-testid="ai-topic" />
          </label>
          <label className="block mb-4">
            <span className="text-[10px] tracking-overline uppercase text-cc-muted">Anahtar Kelimeler</span>
            <input value={aiKeywords} onChange={(e) => setAiKeywords(e.target.value)} className="cc-input mt-1" placeholder="virgülle ayırın" data-testid="ai-keywords" />
          </label>
          <button onClick={aiDraft} disabled={aiBusy || !aiTopic} className="cc-btn w-full justify-center text-[10px] py-2" data-testid="ai-draft-btn">
            {aiBusy ? "Üretiliyor…" : "Taslak Üret"}
          </button>

          <div className="mt-6 pt-6 border-t border-admin-border">
            <button onClick={aiSeo} disabled={aiBusy || !tr.title} className="cc-btn cc-btn-ghost w-full justify-center text-[10px] py-2" data-testid="ai-seo-btn">
              {aiBusy ? "..." : "SEO Önerileri"}
            </button>
            <p className="text-[10px] text-cc-muted mt-2">Başlık + içeriğe göre SEO başlığı, açıklama ve SSS üretir.</p>
          </div>
        </div>
      </aside>
    </div>
  );
}

function BlockEditor({ block, onChange }) {
  if (block.type === "paragraph") {
    return <textarea value={block.text || ""} onChange={(e) => onChange({ text: e.target.value })} className="cc-input min-h-[80px]" placeholder="Paragraf yazın..." />;
  }
  if (block.type === "heading") {
    return (
      <div>
        <select value={block.level || 2} onChange={(e) => onChange({ level: parseInt(e.target.value) })} className="text-xs mb-2 border border-admin-border px-2 py-1">
          <option value={2}>H2</option><option value={3}>H3</option>
        </select>
        <input value={block.text || ""} onChange={(e) => onChange({ text: e.target.value })} className="cc-input font-serif text-2xl" placeholder="Başlık" />
      </div>
    );
  }
  if (block.type === "quote") {
    return (
      <div>
        <textarea value={block.text || ""} onChange={(e) => onChange({ text: e.target.value })} className="cc-input min-h-[60px] italic font-serif" placeholder="Alıntı metni" />
        <input value={block.author || ""} onChange={(e) => onChange({ author: e.target.value })} className="cc-input mt-2 text-sm" placeholder="Yazar / Kaynak" />
      </div>
    );
  }
  if (block.type === "list") {
    return (
      <div>
        <select value={block.style || "ul"} onChange={(e) => onChange({ style: e.target.value })} className="text-xs mb-2 border border-admin-border px-2 py-1">
          <option value="ul">Sırasız</option><option value="ol">Sıralı</option>
        </select>
        {(block.items || [""]).map((it, i) => (
          <div key={i} className="flex items-center gap-2 mb-2">
            <input value={it} onChange={(e) => { const items = (block.items || []).slice(); items[i] = e.target.value; onChange({ items }); }} className="cc-input flex-1" />
            <button onClick={() => { const items = (block.items || []).slice(); items.splice(i, 1); onChange({ items }); }} className="text-cc-muted hover:text-red-600 text-xs">×</button>
          </div>
        ))}
        <button onClick={() => onChange({ items: [...(block.items || []), ""] })} className="text-xs text-cc-sepia">+ Madde ekle</button>
      </div>
    );
  }
  if (block.type === "callout") {
    return (
      <div>
        <select value={block.variant || "tip"} onChange={(e) => onChange({ variant: e.target.value })} className="text-xs mb-2 border border-admin-border px-2 py-1">
          <option value="tip">İpucu</option><option value="info">Bilgi</option><option value="warning">Uyarı</option>
        </select>
        <textarea value={block.text || ""} onChange={(e) => onChange({ text: e.target.value })} className="cc-input min-h-[60px]" />
      </div>
    );
  }
  if (block.type === "image") {
    return (
      <div>
        <input value={block.url || ""} onChange={(e) => onChange({ url: e.target.value })} className="cc-input mb-2" placeholder="Görsel URL" />
        <input value={block.caption || ""} onChange={(e) => onChange({ caption: e.target.value })} className="cc-input mb-2 text-sm" placeholder="Altyazı (caption)" />
        <input value={block.alt || ""} onChange={(e) => onChange({ alt: e.target.value })} className="cc-input text-sm" placeholder="Alt text (SEO)" />
      </div>
    );
  }
  return null;
}
